import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Building2, CalendarClock, FileText, LogOut, Search, ShieldCheck, UserRound } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import api from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  restoreUserSessionSnapshot,
  useAccessToken,
  useClearAuthState,
  useCurrentClient,
  useSetAuthState
} from '@/features/auth/state/auth'
import { useFetchClientByOwner, useFetchClientByCnpjCpf } from '@/features/client/services'
import { useFetchAllAgreementByIdClient } from '@/features/agreement/services/useFetchAllAgreementByIdClient'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import Spinner from '@/components/ui/spinner'
import { Badge } from '@/components/ui/badge'
import { formatCNPJ, formatNome } from '@/utils/format'
import { ROUTE_BUILDERS, ROUTES } from '@/config/routes'
import { formatCurrency, getStatusColor, produtosLabels } from '@/features/products/utils'
import { friendlyNameMaps } from '@/components/form/input/StatusMapper'
import { TypeServico } from '@/lib/enums'
import { formatCPF } from '@/components/form/input/cpf-mask-input'
import ObservationClient from '@/features/client/components/observation-cliente'

const CLIENT_TABS = [
  { value: 'info', label: 'Informações' },
  { value: 'products', label: 'Produtos' },
  { value: 'observations', label: 'Observações' },
  { value: 'agreements', label: 'Minutas' }
] as const

type ClientTabValue = (typeof CLIENT_TABS)[number]['value']

export default function ClientHomePage() {
  const client = useCurrentClient()
  const token = useAccessToken()
  const clearAuth = useClearAuthState()
  const setAuthState = useSetAuthState()
  const navigate = useNavigate()
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>()
  const [activeTab, setActiveTab] = useState<ClientTabValue>('info')
  const [companySearch, setCompanySearch] = useState('')

  const { data: ownerClients = [], isLoading: isLoadingOwnerClients } = useFetchClientByOwner(client?.id)
  const { data: selectedClient, isLoading: isLoadingSelectedClient } = useFetchClientByCnpjCpf(selectedClientId)
  const { data: agreements = [], isLoading: isLoadingAgreements } = useFetchAllAgreementByIdClient(selectedClientId, {
    enabled: !!selectedClientId
  })
  const { data: companyDetails = [], isLoading: isLoadingCompanyDetails } = useQuery({
    queryKey: ['client-portal-company-details', ownerClients.map((item: any) => item.cnpjcpf ?? item.id_client)],
    enabled: Boolean(token && ownerClients.length > 0),
    queryFn: async () => {
      if (!token || ownerClients.length === 0) return []

      const headers = { Authorization: `Bearer ${token}` }

      return Promise.all(
        ownerClients.map(async (item: any) => {
          const companyId = item.cnpjcpf ?? item.id_client
          const response = await api.get(`/clients/${companyId}`, { headers }).catch(() => ({ data: { data: null } }))

          return response.data?.data
            ? {
                companyId,
                ...response.data.data
              }
            : null
        })
      ).then(items => items.filter(Boolean))
    }
  })
  const tenantIds = useMemo(
    () =>
      Array.from(
        new Set(
          (companyDetails ?? [])
            .map((item: any) => String(item?.id_tenant ?? '').trim())
            .filter(Boolean)
        )
      ),
    [companyDetails]
  )
  const { data: tenantDetails = [], isLoading: isLoadingTenantDetails } = useQuery({
    queryKey: ['client-portal-tenants', tenantIds],
    enabled: Boolean(token && tenantIds.length > 0),
    queryFn: async () => {
      if (!token || tenantIds.length === 0) return []

      const headers = { Authorization: `Bearer ${token}` }

      return Promise.all(
        tenantIds.map(async idTenant => {
          const response = await api.get(`/tenants/find-by-cnpj/${idTenant}`, { headers }).catch(() => ({ data: { data: null } }))

          return response.data?.data
            ? {
                id_tenant: idTenant,
                ...response.data.data
              }
            : null
        })
      ).then(items => items.filter(Boolean))
    }
  })
  const { data: productOverview = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ['client-portal-products', selectedClientId],
    enabled: Boolean(token && selectedClientId),
    queryFn: async () => {
      if (!selectedClientId || !token) return []

      const headers = { Authorization: `Bearer ${token}` }
      const productResponse = await api.get(`/products/${selectedClientId}`, { headers })
      const products = Array.isArray(productResponse.data?.data) ? productResponse.data.data : []

      return Promise.all(
        products.map(async (product: any) => {
          const apuracaoResponse = await api.get(`/apuracao/${product.id}`, { headers }).catch(() => ({ data: { data: [] } }))
          const apuracoes = Array.isArray(apuracaoResponse.data?.data) ? apuracaoResponse.data.data : []
          const latestApuracao = apuracoes[0] ?? null

          const resumeResponse =
            latestApuracao?.id != null
              ? await api.get(`/resume/${latestApuracao.id}`, { headers }).catch(() => ({ data: { data: [] } }))
              : { data: { data: [] } }

          const resumes = Array.isArray(resumeResponse.data?.data) ? resumeResponse.data.data : []

          return {
            ...product,
            latestApuracao,
            resumes
          }
        })
      )
    }
  })

  useEffect(() => {
    setActiveTab('info')
  }, [selectedClientId])

  const companyDetailsMap = useMemo(
    () => new Map((companyDetails ?? []).map((item: any) => [item.companyId, item])),
    [companyDetails]
  )
  const tenantDetailsMap = useMemo(
    () => new Map((tenantDetails ?? []).map((item: any) => [String(item.id_tenant ?? item.cnpj ?? ''), item])),
    [tenantDetails]
  )
  const expiredCompanies = useMemo(
    () =>
      ownerClients.filter((item: any) => {
        const companyId = item.cnpjcpf ?? item.id_client
        return isProcuracaoExpired(companyDetailsMap.get(companyId)?.procuracaoValidaAte)
      }),
    [companyDetailsMap, ownerClients]
  )
  const filteredOwnerClients = useMemo(() => {
    const search = companySearch.trim().toLowerCase()
    if (!search) return ownerClients

    return ownerClients.filter((item: any) => {
      const companyId = String(item.cnpjcpf ?? item.id_client ?? '')
      const razaoSocial = String(item.razao_social ?? '').toLowerCase()
      const normalizedDocument = companyId.replace(/\D/g, '')
      const normalizedSearch = search.replace(/\D/g, '')

      return (
        razaoSocial.includes(search) ||
        companyId.toLowerCase().includes(search) ||
        (normalizedSearch.length > 0 && normalizedDocument.includes(normalizedSearch))
      )
    })
  }, [companySearch, ownerClients])
  const selectedCompanyDetail = selectedClientId ? companyDetailsMap.get(selectedClientId) : null
  const selectedTenantName = getTenantDisplayName(
    selectedCompanyDetail?.id_tenant ? tenantDetailsMap.get(String(selectedCompanyDetail.id_tenant)) : null
  )

  const handleLogout = () => {
    const restoredUserSession = restoreUserSessionSnapshot()

    if (restoredUserSession) {
      setAuthState(restoredUserSession)
      navigate(
        restoredUserSession.user?.typeUser === 'ADMIN' ? ROUTES.ADMIN_HOME.path : ROUTES.DASHBOARD_HOME.path,
        { replace: true }
      )
      return
    }

    clearAuth()
    navigate(ROUTES.LOGIN_CLIENT.path, { replace: true })
  }

  if (!client) return null

  if (!client.verifiedOwner) {
    return (
      <div className="min-h-screen bg-muted/30 p-6">
        <div className="mx-auto max-w-3xl">
          <Card>
            <CardHeader>
              <CardTitle>Portal do cliente</CardTitle>
              <CardDescription>
                O acesso completo é liberado somente após a confirmação da assinatura do proprietário.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Proprietário: {formatNome(client.name)} | CPF: {formatCNPJ(client.cpf)}
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={handleLogout}>
                  <LogOut />
                  Sair
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (
    isLoadingOwnerClients ||
    isLoadingCompanyDetails ||
    isLoadingTenantDetails ||
    (selectedClientId && (isLoadingSelectedClient || isLoadingAgreements || isLoadingProducts))
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="large" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-muted/30 px-3 py-4 sm:px-4 md:px-6">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 md:gap-6">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-slate-50 px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-3">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Portal do cliente</h2>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-white">
                    <ShieldCheck className="mr-1 h-4 w-4" />
                    Assinatura validada
                  </Badge>
                  {expiredCompanies.length > 0 && (
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                      {expiredCompanies.length} alerta{expiredCompanies.length > 1 ? 's' : ''} de procuração
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <Button variant="outline" onClick={handleLogout} className="w-full sm:w-auto">
                  <LogOut />
                  Sair
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-2 p-4 sm:grid-cols-2 sm:p-6 xl:grid-cols-2">
            <SummaryCard
              icon={<UserRound className="h-4 w-4" />}
              title="Proprietário"
              value={formatNome(client.name)}
              description={formatCPF(client.cpf)}
            />
            <SummaryCard
              icon={<Building2 className="h-4 w-4" />}
              title="Empresas vinculadas"
              value={String(ownerClients.length)}
              description={
                expiredCompanies.length > 0
                  ? `${expiredCompanies.length} com alerta de procuração`
                  : 'Empresas encontradas pelo CPF'
              }
            />

          </div>
        </div>

        {!selectedClient ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 via-white to-slate-50 px-4 py-4 sm:px-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-slate-800 sm:text-xl">Escolha uma empresa</h2>
                  <p className="mt-1 text-sm text-slate-500">Selecione uma empresa para abrir as informações, minutas e produtos.</p>
                </div>
                <Badge variant="outline" className="bg-white">
                  {filteredOwnerClients.length}/{ownerClients.length}
                </Badge>
              </div>

              {expiredCompanies.length > 0 && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <div className="font-medium">Atenção com procurações</div>
                      <div>
                        {expiredCompanies.length} empresa{expiredCompanies.length > 1 ? 's' : ''} com procuração vencida.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="relative mt-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={companySearch}
                  onChange={event => setCompanySearch(event.target.value)}
                  placeholder="Buscar por empresa ou CNPJ"
                  className="h-10 rounded-xl border-slate-200 bg-white pl-9"
                />
              </div>
            </div>

            <div className="max-h-[72vh] overflow-y-auto">
              {ownerClients.length === 0 && (
                <div className="m-3 rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                  Nenhuma empresa vinculada a este proprietário.
                </div>
              )}

              {ownerClients.length > 0 && filteredOwnerClients.length === 0 && (
                <div className="m-3 rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                  Nenhuma empresa encontrada para essa busca.
                </div>
              )}

              {filteredOwnerClients.length > 0 && (
                <div className="sticky top-0 z-10 grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-slate-100 bg-white/95 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-500 backdrop-blur">
                  <span>Empresa</span>
                  <span>Status</span>
                </div>
              )}

              {filteredOwnerClients.map((item: any) => {
                const companyId = item.cnpjcpf ?? item.id_client
                const companyDetail = companyDetailsMap.get(companyId)
                const procuracaoExpired = isProcuracaoExpired(companyDetail?.procuracaoValidaAte)

                return (
                  <button
                    key={companyId}
                    type="button"
                    onClick={() => setSelectedClientId(companyId)}
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-slate-100 px-3 py-3 text-left transition-all hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-800">{item.razao_social || 'Sem razão social'}</div>
                      <div className="mt-1 break-all font-mono text-xs text-slate-500">{formatCNPJ(companyId)}</div>

                      {companyDetail?.procuracaoValidaAte && (
                        <div className="mt-2">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-full px-2 py-1 text-[11px] font-medium',
                              procuracaoExpired ? 'bg-red-100 text-red-700' : 'bg-emerald-50 text-emerald-700'
                            )}
                          >
                            {procuracaoExpired ? 'Procuração vencida' : `Válida até ${formatPortalDate(companyDetail.procuracaoValidaAte)}`}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <Badge variant="outline">{item.status || 'Sem status'}</Badge>
                      {procuracaoExpired && (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-[11px] font-medium text-red-700">
                          Vencida
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50/60 px-4 py-4 sm:px-6">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-2">
                    <Button variant="ghost" className="h-auto w-fit px-0 text-slate-600" onClick={() => setSelectedClientId(undefined)}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Voltar para lista de empresas
                    </Button>
                    <div className="space-y-1">
                      <h2 className="text-lg font-bold text-slate-800 sm:text-xl">{selectedClient.razao_social}</h2>
                      <p className="break-all text-sm font-mono text-slate-500">{formatCNPJ(selectedClient.cnpjcpf)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="bg-white">
                      Status: {selectedClient.status || '-'}
                    </Badge>
                    <Badge variant="outline" className="bg-white">
                      Criado em: {formatPortalDate(selectedClient.createdDate)}
                    </Badge>
                    {selectedTenantName && (
                      <Badge variant="outline" className="bg-white">
                        Contratada: {selectedTenantName}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b border-slate-100 px-4 sm:px-6">
              <div className="-mx-4 overflow-x-auto px-4 no-scrollbar sm:mx-0 sm:px-0">
                <div className="flex min-w-max items-center gap-5">
                      {CLIENT_TABS.map(tab => (
                        <button
                          key={tab.value}
                          type="button"
                          onClick={() => setActiveTab(tab.value)}
                          className={cn(
                            'relative shrink-0 whitespace-nowrap py-4 text-sm font-medium transition-colors',
                            activeTab === tab.value ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
                          )}
                        >
                          {tab.label}
                          {activeTab === tab.value && (
                            <motion.div
                              layoutId="client-home-active-tab"
                              className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-blue-600"
                              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                            />
                          )}
                        </button>
                      ))}
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${selectedClientId}-${activeTab}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  className="space-y-4"
                >
                  {activeTab === 'info' && (
                    <>
                      <div className="rounded-xl border bg-muted/10 p-4">
                        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground">
                          <CalendarClock className="h-4 w-4" />
                          Procuração
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <InfoField
                            label="Status da procuração"
                            value={selectedClient.procuracaoHabilitada}
                            valueClassName={getProcuracaoColor(selectedClient.procuracaoHabilitada)}
                          />
                          <InfoField label="Habilitada em" value={formatPortalDate(selectedClient.procuracaoHabilitadaEm)} />
                          <InfoField
                            label="Válida até"
                            value={formatPortalDate(selectedClient.procuracaoValidaAte)}
                            valueClassName={
                              isProcuracaoExpired(selectedClient.procuracaoValidaAte) ? 'font-semibold text-red-600' : undefined
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="text-sm font-medium text-foreground">Contatos</div>
                        {(selectedClient.contacts ?? []).length === 0 && (
                          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                            Nenhum contato adicional encontrado.
                          </div>
                        )}

                        {(selectedClient.contacts ?? []).map((contact: any) => (
                          <div key={contact.id} className="rounded-xl border p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="font-medium">{contact.nome ? formatNome(contact.nome) : 'Sem nome'}</div>
                              <Badge variant="outline">{getContactTypeLabel(contact.type)}</Badge>
                            </div>
                            <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                              <InfoField label="Documento" value={contact.document} />
                              <InfoField label="Contato" value={contact.contato} />
                              <InfoField label="E-mail" value={contact.email} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {activeTab === 'products' && (
                          <>
                            {productOverview.length === 0 && (
                              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                                Nenhum produto encontrado para esta empresa.
                              </div>
                            )}

                            {productOverview.map((product: any) => (
                              <div key={product.id} className="rounded-xl border p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                  <div className="space-y-2">
                                    <div className="font-medium">
                                      {friendlyNameMaps.typeServico?.[product.typeServico] ??
                                        produtosLabels[product.typeServico as TypeServico] ??
                                        product.typeServico}
                                    </div>
                                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                                      <span>Regime: {product.typeRegime || '-'}</span>
                                      <span>Última apuração: {formatPortalDate(product.latestApuracao?.createdDate)}</span>
                                    </div>
                                  </div>

                                  <span
                                    className={cn(
                                      'inline-flex rounded-full px-3 py-1 text-xs font-semibold text-white',
                                      getStatusColor(product.status)
                                    )}
                                  >
                                    {friendlyNameMaps.produtosStatus?.[product.status] ?? product.status}
                                  </span>
                                </div>

                                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                  {buildVisibleResumeItems(product.resumes).length > 0 ? (
                                    buildVisibleResumeItems(product.resumes).map((resume: any) => (
                                      <InfoField
                                        key={resume.id}
                                        label={formatResumeLabel(resume.typeValue)}
                                        value={formatResumeValue(resume.value)}
                                      />
                                    ))
                                  ) : (
                                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground md:col-span-2 xl:col-span-3">
                                      Nenhum resumo disponível para a apuração mais recente deste produto.
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        )}

                  {activeTab === 'observations' && <ObservationClient type="GERAL" cnpj={selectedClient.cnpjcpf} />}

                  {activeTab === 'agreements' && (
                          <>
                            {agreements.length === 0 && (
                              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                                Nenhuma minuta encontrada para esta empresa.
                              </div>
                            )}

                            {agreements.map((agreement: any) => (
                              <div key={agreement.id} className="rounded-xl border p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                  <div className="space-y-2">
                                    <div className="font-medium">
                                      {Array.isArray(agreement.contracts) && agreement.contracts.length > 0
                                        ? agreement.contracts.join(', ')
                                        : 'Minuta'}
                                    </div>
                                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                                      <span>Regime: {agreement.typeRegime || '-'}</span>
                                      <span>Status: {agreement.status || '-'}</span>
                                      <span>Criada em: {formatPortalDate(agreement.createdDate)}</span>
                                    </div>
                                  </div>

                                  <Button
                                    className="w-full sm:w-auto"
                                    variant="outline"
                                    onClick={() =>
                                      navigate(ROUTE_BUILDERS.clientHomeMinute(agreement.id_client || selectedClientId || agreement.id))
                                    }
                                  >
                                    <FileText />
                                    Abrir minuta
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SummaryCard({
  icon,
  title,
  value,
  description
}: {
  icon: JSX.Element
  title: string
  value: string
  description: string
}) {
  return (
    <div className="rounded-xl border p-3 sm:p-4">
      <div className="flex items-start justify-between gap-3 sm:block">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground sm:text-sm sm:normal-case sm:tracking-normal">
            {icon}
            <span className="truncate">{title}</span>
          </div>
          <div className="mt-1 text-lg font-semibold leading-none sm:mt-2 sm:text-2xl">{value}</div>
        </div>
        <div className="max-w-[140px] text-right text-[11px] leading-4 text-muted-foreground sm:mt-1 sm:max-w-none sm:text-left sm:text-sm">
          {description}
        </div>
      </div>
    </div>
  )
}

function InfoField({
  label,
  value,
  valueClassName,
  className
}: {
  label: string
  value?: string | null
  valueClassName?: string
  className?: string
}) {
  return (
    <div className={cn('rounded-xl border bg-muted/20 p-3', className)}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-1 break-words text-sm text-foreground', valueClassName)}>{value || '-'}</div>
    </div>
  )
}

function buildVisibleResumeItems(resumes: any[]) {
  return (Array.isArray(resumes) ? resumes : []).filter(resume => resume?.isActive !== false).slice(0, 6)
}

function formatResumeLabel(value?: string | null) {
  if (!value) return 'Resumo'

  return value
    .split('_')
    .map(part => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ')
}

function formatResumeValue(value: unknown) {
  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return String(value ?? '-')
  return formatCurrency(numericValue)
}

function getContactTypeLabel(type?: string | null) {
  if (type === 'OWNER') return 'Proprietário'
  if (type === 'ACCOUNTANT') return 'Contador'
  if (!type) return 'Contato'
  return type
}

function formatPortalDate(value?: string | Date | null) {
  if (!value) return '-'

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('pt-BR').format(date)
}

function getProcuracaoColor(value?: string | null) {
  return String(value ?? '').toUpperCase().includes('HABILITADA') ? 'font-semibold text-green-600' : 'font-semibold text-red-600'
}

function isProcuracaoExpired(value?: string | Date | null) {
  if (!value) return false

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return false

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  date.setHours(0, 0, 0, 0)

  return date < today
}

function getTenantDisplayName(tenant?: { nome_fantasia?: string | null; razao_social?: string | null; cnpj?: string | null } | null) {
  return tenant?.nome_fantasia || tenant?.razao_social || tenant?.cnpj || 'Tenant não identificado'
}
